import { useState, useEffect } from 'react'
import { useAuth } from '../lib/auth-context'

interface SettingsProps {
  open: boolean
  onClose: () => void
  selectedModel: string
  onModelChange: (model: string) => void
  apiKey: string
  onApiKeyChange: (key: string) => void
}

const MODELS = [
  { id: 'qwen/qwen3.5-flash-20260224', name: 'Qwen 3.5 Flash', desc: 'Fastest, best value' },
  { id: 'qwen/qwen3.5-35b-a3b', name: 'Qwen 3.5 35B A3B', desc: 'Lightweight, efficient' },
  { id: 'qwen/qwen3.5-122b-a10b', name: 'Qwen 3.5 122B A10B', desc: 'Mid-size, strong' },
  { id: 'qwen/qwen3.5-397b-a17b', name: 'Qwen 3.5 397B A17B', desc: 'Largest Qwen 3.5, premium' },
  { id: 'qwen/qwen3.7-plus', name: 'Qwen 3.7 Plus', desc: 'Balanced & capable' },
  { id: 'qwen/qwen3.7-max', name: 'Qwen 3.7 Max', desc: 'Best quality' },
]

const PROFILE_TABS = ['profile', 'api', 'model', 'usage'] as const
type ProfileTab = typeof PROFILE_TABS[number]

export function Settings({ open, onClose, selectedModel, onModelChange, apiKey, onApiKeyChange }: SettingsProps) {
  const { user, profile, settings, saveProfile, saveSettings, signOut } = useAuth()
  const [tab, setTab] = useState<ProfileTab>('profile')
  const [showKey, setShowKey] = useState(false)
  const [name, setName] = useState(profile?.name || '')
  const [religion, setReligion] = useState(profile?.religion || '')
  const [belief, setBelief] = useState(profile?.belief || '')

  useEffect(() => {
    if (profile) {
      setName(profile.name || '')
      setReligion(profile.religion || '')
      setBelief(profile.belief || '')
    }
  }, [profile])

  if (!open) return null

  async function handleSaveProfile() {
    await saveProfile({ name: name.trim() || null, religion: religion || null, belief: belief || null })
  }

  async function handleSaveApiKey() {
    await saveSettings({ openrouter_key: apiKey || null })
  }

  const tabs = [
    { id: 'profile' as const, label: 'Profile' },
    { id: 'api' as const, label: 'API Key' },
    { id: 'model' as const, label: 'Model' },
    { id: 'usage' as const, label: 'Usage' },
  ]

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-panel modal-panel-md card p-0" onClick={e => e.stopPropagation()}>
        <div className="card-header">
          <h2 className="text-h4">Settings</h2>
          <button onClick={onClose} className="btn btn-ghost btn-icon" aria-label="Close">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M4.5 4.5l9 9M13.5 4.5l-9 9" />
            </svg>
          </button>
        </div>

        <div className="tab-bar border-b border-[hsl(var(--border))] px-4">
          {tabs.map(({ id, label }) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`tab-item ${tab === id ? 'active' : ''}`}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="card-body space-y-5">
          {tab === 'profile' && (
            <>
              <div className="row gap-4">
                {user?.user_metadata?.avatar_url ? (
                  <img src={user.user_metadata.avatar_url} alt="" className="avatar avatar-user" />
                ) : (
                  <div className="avatar avatar-user text-h4 font-bold">
                    {(name || '?')[0].toUpperCase()}
                  </div>
                )}
                <div>
                  <div className="text-small font-medium text-[hsl(var(--fg))]">{user?.email}</div>
                  <div className="text-caption text-[hsl(var(--fg-muted))]">{profile?.religion || 'No religion set'}</div>
                </div>
              </div>

              <div>
                <label className="field-label">Display Name</label>
                <input type="text" value={name} onChange={e => setName(e.target.value)} className="input-field" />
              </div>
              <button onClick={handleSaveProfile} className="btn btn-primary">Save</button>

              <hr className="divider" />

              <button onClick={signOut} className="btn btn-secondary w-full justify-center border-[hsl(var(--error)/0.3)] text-[hsl(var(--error))] hover:bg-[hsl(var(--error-bg))]">
                Sign Out
              </button>
            </>
          )}

          {tab === 'api' && (
            <div>
              <h3 className="text-h4">OpenRouter API Key</h3>
              <p className="text-small text-[hsl(var(--fg-muted))] mb-4">
                Get your key from <a href="https://openrouter.ai/keys" target="_blank" rel="noopener noreferrer" className="text-[hsl(var(--accent))] underline">openrouter.ai/keys</a>. It&apos;s encrypted in your account settings.
              </p>
              <div className="relative">
                <input
                  type={showKey ? 'text' : 'password'}
                  value={apiKey}
                  onChange={e => onApiKeyChange(e.target.value)}
                  placeholder="sk-or-v1-..."
                  className="input-field font-mono pr-10"
                />
                <button
                  onClick={() => setShowKey(!showKey)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[hsl(var(--fg-muted))] hover:text-[hsl(var(--fg))]"
                >
                  {showKey ? (
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M1 8s2.5-5 7-5 7 5 7 5-2.5 5-7 5-7-5-7-5z" /><circle cx="8" cy="8" r="2.5" />
                      <path d="M12 4l-8 8" />
                    </svg>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M1 8s2.5-5 7-5 7 5 7 5-2.5 5-7 5-7-5-7-5z" /><circle cx="8" cy="8" r="2.5" />
                    </svg>
                  )}
                </button>
              </div>
              <button onClick={handleSaveApiKey} className="btn btn-primary mt-4">Save Key</button>
            </div>
          )}

          {tab === 'model' && (
            <div>
              <h3 className="text-h4">LLM Model</h3>
              <p className="text-small text-[hsl(var(--fg-muted))] mb-4">Choose which OpenRouter model powers the AI.</p>
              <div className="space-y-2">
                {MODELS.map(model => (
                  <button
                    key={model.id}
                    onClick={() => onModelChange(model.id)}
                    className={`w-full text-left rounded-lg border p-3 transition-all ${
                      selectedModel === model.id
                        ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent-soft))]'
                        : 'border-[hsl(var(--border))] hover:border-[hsl(var(--accent)/0.3)]'
                    }`}
                  >
                    <div className="text-small font-medium text-[hsl(var(--fg))]">{model.name}</div>
                    <div className="text-caption text-[hsl(var(--fg-muted))]">{model.desc}</div>
                  </button>
                ))}
              </div>
              <div className="mt-4">
                <label className="field-label">
                  Custom model ID
                  {!MODELS.some(m => m.id === selectedModel) && (
                    <span className="text-[hsl(var(--accent))] ml-1">(active)</span>
                  )}
                </label>
                <input
                  type="text"
                  placeholder="e.g., openai/o1-preview"
                  value={selectedModel}
                  onChange={e => onModelChange(e.target.value)}
                  className="input-field"
                />
              </div>
            </div>
          )}

          {tab === 'usage' && (
            <div>
              <h3 className="text-h4">Usage & Cost</h3>
              <p className="text-small text-[hsl(var(--fg-muted))] mb-4">
                Total OpenRouter API cost across all conversations.
              </p>
              <div className="card">
                <div className="text-h2 text-[hsl(var(--fg))]">
                  ${parseFloat(localStorage.getItem('islam-preacher-total-cost') || '0').toFixed(6)}
                </div>
                <div className="text-caption text-[hsl(var(--fg-muted))] mt-1">Total cost</div>
              </div>
              <button
                onClick={() => {
                  localStorage.setItem('islam-preacher-total-cost', '0')
                  window.location.reload()
                }}
                className="btn btn-secondary mt-4 w-full justify-center border-[hsl(var(--error)/0.3)] text-[hsl(var(--error))] hover:bg-[hsl(var(--error-bg))]"
              >
                Reset Cost
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
